import React, { MouseEventHandler, PropsWithChildren, useCallback } from 'react';
import classNames from 'classnames';

import { MenuLayerLevel } from '../types';

import { SsrModule } from 'app/constants/ssrModules';

import { isInLayer, isMenuItem } from '../dom-utils';
import {
  LeftMenuStateAPIContext,
  useLeftMenuStateAPIFactory,
} from '../hooks/left-menu-state-api';
import { activeLevelStyles, hoveredLevelStyles } from '../styles/container-state-styles';
import ExpandControl from './ExpandControl';

import styles from '../styles/LeftMenu.module.scss';

export type ContainerProps = PropsWithChildren<{
  activeLevel: MenuLayerLevel;
  noRoot?: boolean;
  ssrDataId?: SsrModule;
}>;

const LeftMenuContainer: React.FC<ContainerProps> = ({
  activeLevel,
  noRoot = false,
  ssrDataId,
  children,
}) => {
  const leftMenuStateAPI = useLeftMenuStateAPIFactory(activeLevel);

  const {
    collapsed,
    expanded,
    resetExpanded,
    hoveredLayerLevel,
    resetHoveredLayerLevel,
  } = leftMenuStateAPI;

  const handleMouseLeave = useCallback<MouseEventHandler>(
    () => resetHoveredLayerLevel(),
    [resetHoveredLayerLevel],
  );

  const handleItemClick = useCallback<MouseEventHandler>(
    (event) => {
      const target = event.target as HTMLElement;

      if (expanded && (isMenuItem(target) || isInLayer(target))) {
        resetExpanded();
      }
      else if (isMenuItem(target)) {
        resetHoveredLayerLevel();
      }
    },
    [expanded, resetExpanded],
  );

  // --

  const rootOnly = (activeLevel === '0');

  const className = classNames(
    styles.container,
    activeLevelStyles[activeLevel],
    {
      [styles.noRoot]: noRoot,
      [styles.expanded]: expanded,
      [styles.collapsed]: collapsed,
    },
    (hoveredLayerLevel && hoveredLevelStyles[hoveredLayerLevel]),
  );

  return (
    <div className={className}
         data-ssr-id={ssrDataId}
         data-test="left-menu"
         data-test-root-menu-state={classNames({ 'full-size': rootOnly, expanded, collapsed })}
         data-test-active-level={activeLevel}
         onMouseLeave={handleMouseLeave}
         onClick={handleItemClick}>
      <LeftMenuStateAPIContext.Provider value={leftMenuStateAPI}>
        <ExpandControl />
        {children}
      </LeftMenuStateAPIContext.Provider>
    </div>
  );
};

export default LeftMenuContainer;
